import React, { createContext, useContext, useReducer, useCallback, ReactNode } from 'react';
import { domainAPI } from '../services/api';
import type { Domain } from '../types';

// Action types
const DOMAIN_ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  SET_DOMAINS: 'SET_DOMAINS',
  ADD_DOMAIN: 'ADD_DOMAIN',
  UPDATE_DOMAIN: 'UPDATE_DOMAIN',
  DELETE_DOMAIN: 'DELETE_DOMAIN',
  SET_SELECTED_DOMAIN: 'SET_SELECTED_DOMAIN',
  SET_VERIFICATION_STATUS: 'SET_VERIFICATION_STATUS',
  CLEAR_ERROR: 'CLEAR_ERROR'
} as const;

// State type
interface DomainState {
  domains: Domain[];
  loading: boolean;
  error: string | null;
  selectedDomain: Domain | null;
  verificationStatus: Record<string, string>;
}

// Action types
type DomainAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string }
  | { type: 'CLEAR_ERROR' }
  | { type: 'SET_DOMAINS'; payload: Domain[] }
  | { type: 'ADD_DOMAIN'; payload: Domain }
  | { type: 'UPDATE_DOMAIN'; payload: Domain }
  | { type: 'DELETE_DOMAIN'; payload: string }
  | { type: 'SET_SELECTED_DOMAIN'; payload: Domain | null }
  | { type: 'SET_VERIFICATION_STATUS'; payload: { domainId: string; status: string } };

// Context type
interface DomainContextType extends DomainState {
  primaryDomain: Domain | undefined;
  activeDomains: Domain[];
  pendingDomains: Domain[];
  
  // Actions
  setLoading: (loading: boolean) => void;
  setError: (error: string) => void;
  clearError: () => void;
  setSelectedDomain: (domain: Domain | null) => void;
  
  // API methods
  fetchDomains: () => Promise<void>;
  createDomain: (domainData: { domain: string }) => Promise<any>;
  updateDomain: (domainId: string, domainData: Record<string, any>) => Promise<any>;
  deleteDomain: (domainId: string) => Promise<void>;
  verifyDNS: (domainId: string) => Promise<any>;
  verifySSL: (domainId: string) => Promise<any>;
  setPrimaryDomain: (domainId: string) => Promise<any>;
  addDNSRecord: (domainId: string, recordData: Record<string, any>) => Promise<any>;
  deleteDNSRecord: (domainId: string, recordId: string) => Promise<void>;
}

// Initial state
const initialState: DomainState = {
  domains: [],
  loading: false,
  error: null,
  selectedDomain: null,
  verificationStatus: {}
};

// Reducer
const domainReducer = (state: DomainState, action: DomainAction): DomainState => {
  switch (action.type) {
    case DOMAIN_ACTIONS.SET_LOADING:
      return { ...state, loading: action.payload };
    
    case DOMAIN_ACTIONS.SET_ERROR:
      return { ...state, error: action.payload, loading: false };
    
    case DOMAIN_ACTIONS.CLEAR_ERROR:
      return { ...state, error: null };
    
    case DOMAIN_ACTIONS.SET_DOMAINS:
      return { ...state, domains: action.payload, loading: false };
    
    case DOMAIN_ACTIONS.ADD_DOMAIN:
      return { 
        ...state, 
        domains: [action.payload, ...state.domains],
        loading: false 
      };
    
    case DOMAIN_ACTIONS.UPDATE_DOMAIN:
      return {
        ...state,
        domains: state.domains.map(domain => 
          domain._id === action.payload._id ? action.payload : domain
        ),
        selectedDomain: state.selectedDomain?._id === action.payload._id 
          ? action.payload 
          : state.selectedDomain,
        loading: false
      };
    
    case DOMAIN_ACTIONS.DELETE_DOMAIN:
      return {
        ...state,
        domains: state.domains.filter(domain => domain._id !== action.payload),
        selectedDomain: state.selectedDomain?._id === action.payload 
          ? null 
          : state.selectedDomain,
        loading: false
      };
    
    case DOMAIN_ACTIONS.SET_SELECTED_DOMAIN:
      return { ...state, selectedDomain: action.payload };
    
    case DOMAIN_ACTIONS.SET_VERIFICATION_STATUS:
      return {
        ...state,
        verificationStatus: {
          ...state.verificationStatus,
          [action.payload.domainId]: action.payload.status
        }
      };
    
    default:
      return state;
  }
};

// Create context
const DomainContext = createContext<DomainContextType | undefined>(undefined);

// Provider component
interface DomainProviderProps {
  children: ReactNode;
}

export const DomainProvider = ({ children }: DomainProviderProps): React.ReactElement => {
  const [state, dispatch] = useReducer(domainReducer, initialState);

  // Action creators
  const setLoading = useCallback((loading: boolean): void => {
    dispatch({ type: DOMAIN_ACTIONS.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error: string): void => {
    dispatch({ type: DOMAIN_ACTIONS.SET_ERROR, payload: error });
  }, []);

  const clearError = useCallback((): void => {
    dispatch({ type: DOMAIN_ACTIONS.CLEAR_ERROR });
  }, []);

  const setSelectedDomain = useCallback((domain: Domain | null): void => {
    dispatch({ type: DOMAIN_ACTIONS.SET_SELECTED_DOMAIN, payload: domain });
  }, []);

  // API methods
  const fetchDomains = useCallback(async (): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      const response = await domainAPI.getDomains();
      const domains = response.data || response;
      dispatch({ type: DOMAIN_ACTIONS.SET_DOMAINS, payload: domains });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch domains';
      setError(message);
    }
  }, [setLoading, clearError, setError]);

  const createDomain = useCallback(async (domainData: { domain: string }): Promise<any> => {
    try {
      setLoading(true);
      clearError();
      const response = await domainAPI.createDomain(domainData);
      const newDomain = response.data || response;
      dispatch({ type: DOMAIN_ACTIONS.ADD_DOMAIN, payload: newDomain });
      return newDomain;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create domain';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const updateDomain = useCallback(async (domainId: string, domainData: Record<string, any>): Promise<any> => {
    try {
      setLoading(true);
      clearError();
      const response = await domainAPI.updateDomain(domainId, domainData);
      const updatedDomain = response.data || response;
      dispatch({ type: DOMAIN_ACTIONS.UPDATE_DOMAIN, payload: updatedDomain });
      return updatedDomain;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update domain';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const deleteDomain = useCallback(async (domainId: string): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      await domainAPI.deleteDomain(domainId);
      dispatch({ type: DOMAIN_ACTIONS.DELETE_DOMAIN, payload: domainId });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete domain';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const verifyDNS = useCallback(async (domainId: string): Promise<any> => {
    try {
      setLoading(true);
      clearError();
      const result = await domainAPI.verifyDNS(domainId);
      const domain = result.data?.domain || result.domain;
      if (domain) {
        dispatch({ type: DOMAIN_ACTIONS.UPDATE_DOMAIN, payload: domain });
      }
      dispatch({ 
        type: DOMAIN_ACTIONS.SET_VERIFICATION_STATUS, 
        payload: { domainId, status: 'dns-verified' }
      });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to verify DNS';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const verifySSL = useCallback(async (domainId: string): Promise<any> => {
    try {
      setLoading(true);
      clearError();
      const result = await domainAPI.verifySSL(domainId);
      const domain = result.data?.domain || result.domain;
      if (domain) {
        dispatch({ type: DOMAIN_ACTIONS.UPDATE_DOMAIN, payload: domain });
      }
      dispatch({ 
        type: DOMAIN_ACTIONS.SET_VERIFICATION_STATUS, 
        payload: { domainId, status: 'ssl-verified' }
      });
      return result;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to verify SSL';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const setPrimaryDomain = useCallback(async (domainId: string): Promise<any> => {
    try {
      setLoading(true);
      clearError();
      const response = await domainAPI.setPrimaryDomain(domainId);
      const updatedDomain = response.data || response;
      dispatch({ type: DOMAIN_ACTIONS.UPDATE_DOMAIN, payload: updatedDomain });
      return updatedDomain;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to set primary domain';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const addDNSRecord = useCallback(async (domainId: string, recordData: Record<string, any>): Promise<any> => {
    try {
      setLoading(true);
      clearError();
      const newRecord = await domainAPI.addDNSRecord(domainId, recordData);
      // Refresh the domain to get updated DNS records
      const response = await domainAPI.getDomain(domainId);
      const updatedDomain = response.data || response;
      dispatch({ type: DOMAIN_ACTIONS.UPDATE_DOMAIN, payload: updatedDomain });
      return newRecord;
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to add DNS record';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  const deleteDNSRecord = useCallback(async (domainId: string, recordId: string): Promise<void> => {
    try {
      setLoading(true);
      clearError();
      await domainAPI.deleteDNSRecord(domainId, recordId);
      // Refresh the domain to get updated DNS records
      const response = await domainAPI.getDomain(domainId);
      const updatedDomain = response.data || response;
      dispatch({ type: DOMAIN_ACTIONS.UPDATE_DOMAIN, payload: updatedDomain });
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to delete DNS record';
      setError(message);
      throw error;
    }
  }, [setLoading, clearError, setError]);

  // Computed values
  // Safety check: ensure domains is always an array
  const domains = Array.isArray(state.domains) ? state.domains : [];
  const primaryDomain = domains.find(domain => domain.isPrimary);
  const activeDomains = domains.filter(domain => (domain as any).status === 'active');
  const pendingDomains = domains.filter(domain => (domain as any).status === 'pending');

  const value = {
    // State
    ...state,
    domains, // Use the safe domains array
    primaryDomain,
    activeDomains,
    pendingDomains,
    
    // Actions
    setLoading,
    setError,
    clearError,
    setSelectedDomain,
    
    // API methods
    fetchDomains,
    createDomain,
    updateDomain,
    deleteDomain,
    verifyDNS,
    verifySSL,
    setPrimaryDomain,
    addDNSRecord,
    deleteDNSRecord
  };

  return (
    <DomainContext.Provider value={value}>
      {children}
    </DomainContext.Provider>
  );
};

// Custom hook
export const useDomainContext = (): DomainContextType => {
  const context = useContext(DomainContext);
  if (!context) {
    throw new Error('useDomainContext must be used within a DomainProvider');
  }
  return context;
};

export default DomainContext; 